import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useLocation } from 'react-router-dom';
import { baseurl } from '../utility/helper';
import {
  StatusBadge, rupee, formatDate, formatDateTime,
} from '../utility/instituteHelpers';
import InstituteDataTable, { esc, badgeHtml } from '../utility/InstituteDataTable';
import '../assets/css/Institute.css';

// Same ID-type labels CandidateAdd uses for its "ID Proof" column.
const ID_TYPE_LABEL = { aadhar: 'Aadhar', pan: 'PAN', dl: 'DL', pass: 'Passport', na: 'N/A' };
const idProofDisplay = (idType, idValue) => (idValue ? `${ID_TYPE_LABEL[idType] || idType || ''}: ${idValue}` : '—');

const ucFirst = (g) => (g ? g.charAt(0).toUpperCase() + g.slice(1).toLowerCase() : '—');
const displayOr = (fmt) => (d, t) => (t === 'display' ? fmt(d) : (d ?? ''));

const testColumns = [
  {
    title: 'Test', data: 'test_name',
    render: (d, t, r) => (t === 'display'
      ? `<a href="#!" data-action="open" data-key="${esc(r.institute_test_id)}">${esc(d)}</a>` : (d ?? '')),
  },
  { title: 'Code', data: 'test_code' },
  { title: 'Start', data: 'test_start_time', render: (d, t) => (t === 'display' ? (formatDateTime(d) || '—') : (d ? new Date(d).getTime() : 0)) },
  { title: 'Candidates', data: 'candidate_count' },
  { title: 'Status', data: 'status', render: (d, t) => (t === 'display' ? badgeHtml(d) : (d ?? '')) },
];

const candidateColumns = [
  { title: 'Sr.No', data: null, orderable: false, render: (d, t, r, meta) => meta.row + 1 },
  {
    title: 'CandidateNo', data: 'test_candidate_id',
    render: (d, t) => (t === 'display'
      ? `<a href="/admin/CandiAdd/${esc(d)}" target="_blank" rel="noreferrer">${esc(d)}</a>` : (d ?? '')),
  },
  { title: 'Candidate', data: null, render: (d, t, r) => `${esc(r.first_name)} ${esc(r.last_name)}`.trim() },
  {
    title: 'PI', data: 'candidate_img', orderable: false, searchable: false, notexport: true,
    render: (d) => (d
      ? `<img src="${esc(d)}" alt="" width="34" height="34" style="border-radius:50%;object-fit:cover"/>`
      : '<span class="text-muted" style="font-size:10px">No Img</span>'),
  },
  { title: 'Mobile', data: 'mobile_no', render: (d) => esc(d) },
  { title: 'Email', data: 'email', render: (d) => esc(d) },
  { title: 'Gender', data: 'gender', render: (d) => ucFirst(d) },
  { title: 'Register Date', data: 'joining_date', render: displayOr((d) => (d ? formatDate(d) : '—')) },
  {
    title: 'Test', data: 'test_name',
    render: (d, t, r) => (d
      ? (t === 'display' ? `${esc(d)} <small class="text-muted">(${esc(r.test_code)})</small>` : `${d} (${r.test_code})`)
      : (t === 'display' ? '<span class="text-muted">—</span>' : '')),
  },
  { title: 'Amount', data: 'amount', render: displayOr(rupee) },
  { title: 'ID Proof', data: 'aadhar_last', render: (d, t, r) => (t === 'display' ? esc(idProofDisplay(r.id_type, d)) : idProofDisplay(r.id_type, d)) },
  { title: 'Status', data: 'status', render: (d, t) => (t === 'display' ? badgeHtml(d) : (d ?? '')) },
  {
    title: 'Assign Test', data: null, orderable: false, searchable: false, notexport: true,
    render: (d, t, r) => `<button class="btn btn-primary btn-sm" data-action="assign" data-key="${esc(r._key)}">🎯 Assign Test</button>`,
  },
];

// The batch link is the public website's page, not this admin app.
// const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || 'https://mensaindia.org';
    const WEBSITE_URL ='http://localhost:3001'
const BatchDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const batchId = location.state?.batchId;

  const [overview, setOverview] = useState(null);
  const [tests, setTests] = useState([]);
  const [candidates, setCandidates] = useState([]);

  const loadAll = useCallback(async () => {
    if (!batchId) return;
    try {
      const [ov, ts, cs] = await Promise.all([
        axios.get(`${baseurl}/admin/batches/${batchId}/dashboard`),
        axios.get(`${baseurl}/admin/batches/${batchId}/tests`),
        axios.get(`${baseurl}/admin/batches/${batchId}/candidates`),
      ]);
      setOverview(ov.data);
      setTests(ts.data || []);
      setCandidates((cs.data || []).map((r) => ({ ...r, _key: r.btc_id || `c-${r.test_candidate_id}` })));
    } catch (err) {
      console.error('Error loading batch dashboard:', err);
    }
  }, [batchId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (!batchId) {
    return (
      <div className="content container-fluid inst-wrap">
        <div className="inst-card">No batch selected. <button className="btn btn-link" onClick={() => navigate('/admin/BatchList')}>Go to Batches</button></div>
      </div>
    );
  }

  const batch = overview?.batch;
  const activeTests = tests.filter((t) => t.status === 'Active');

  const copyLink = () => {
    const link = `${WEBSITE_URL}${overview.batch_link}`;
    navigator.clipboard?.writeText(link);
    Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Batch link copied', showConfirmButton: false, timer: 1500 });
  };

  // Add / bulk-upload candidates through the regular CandidateAdd page (same APIs),
  // flagged as institute candidates of this batch.
  const openCandidateAdd = (view) => navigate('/admin/CandiAdd', {
    state: { batchCtx: { view, batchId, instituteId: batch?.institute_id } },
  });

  // Assign one of this batch's own institute tests (institute_test = 1) to a
  // candidate — same /admin/candidates/assign-test API CandidateAdd's own
  // "Assign Test" feature uses, just scoped to this batch's Active tests.
  const assignTest = async (row) => {
    if (!activeTests.length) return Swal.fire('No Active test', 'This batch has no Active test to assign yet.', 'info');
    const optionsHtml = activeTests
      .map((t) => `<option value="${t.institute_test_id}" ${t.institute_test_id === row.institute_test_id ? 'selected' : ''}>${t.test_name} (${t.test_code})</option>`)
      .join('');
    const { isConfirmed, value } = await Swal.fire({
      title: 'Assign Test',
      html: `<div style="text-align:left">
        Assign <b>${row.first_name} ${row.last_name}</b> to:
        <select id="as-test" class="swal2-select" style="display:block;width:100%;box-sizing:border-box;margin:8px 0 0">${optionsHtml}</select>
        <input id="as-amount" type="number" min="0" class="swal2-input" style="width:100%;box-sizing:border-box;margin:12px 0 0" placeholder="Amount (₹)" value="${row.amount || ''}"/>
      </div>`,
      showCancelButton: true, confirmButtonText: 'Assign',
      preConfirm: () => {
        const testId = document.getElementById('as-test').value;
        const amount = document.getElementById('as-amount').value;
        if (!amount || isNaN(amount)) { Swal.showValidationMessage('Enter a valid amount'); return false; }
        return { testId, amount };
      },
    });
    if (!isConfirmed) return;
    try {
      await axios.post(`${baseurl}/admin/candidates/assign-test`, {
        candidate_ids: [row.test_candidate_id], test_id: value.testId, amount: value.amount,
      });
      Swal.fire('Assigned', 'Test assigned to the candidate.', 'success');
      loadAll();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || err.response?.data?.message || 'Failed to assign test', 'error');
    }
  };

  return (
    <div className="content container-fluid inst-wrap">
      <center><h2><b>Batch Dashboard</b></h2></center>

      {/* Overview */}
      <div className="inst-card">
        {batch ? (
          <>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
              <div>
                <h4 className="mb-1">{batch.batch_name} <small className="text-muted">({batch.batch_code})</small></h4>
                <div className="text-muted">{batch.institute_name} — {batch.institute_location}</div>
                {batch.chapter_name && <div className="text-muted">Chapter: {batch.chapter_name}</div>}
              </div>
              <div className="text-end">
                <StatusBadge status={batch.status} />
              </div>
            </div>

            <div className="inst-overview-grid">
              <div className="inst-overview-tile"><div className="label">Requested Fee</div><div className="value">{rupee(batch.requested_fee)}</div></div>
              <div className="inst-overview-tile"><div className="label">Approved Snapshot</div><div className="value">{rupee(batch.approved_fee_snapshot)}</div></div>
              <div className="inst-overview-tile"><div className="label">Fee Difference</div><div className="value">{Number(batch.fee_difference) === 0 ? '—' : rupee(batch.fee_difference)}</div></div>
              <div className="inst-overview-tile"><div className="label">Tests</div><div className="value">{overview.test_count}</div></div>
              <div className="inst-overview-tile"><div className="label">Registered Candidates</div><div className="value">{overview.candidate_count}</div></div>
              <div className="inst-overview-tile">
                <div className="label">Period</div>
                <div className="value" style={{ fontSize: '0.95rem' }}>
                  {batch.start_date ? formatDate(batch.start_date) : '—'}{batch.end_date ? ` → ${formatDate(batch.end_date)}` : ''}
                </div>
              </div>
            </div>

            <div className="mt-3">
              <div className="label text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>Batch Link</div>
              <div className="inst-link-box mt-1">
                <span>{WEBSITE_URL}{overview.batch_link}</span>
                <button className="btn btn-sm btn-outline-secondary" onClick={copyLink}>Copy</button>
              </div>
            </div>
          </>
        ) : <div>Loading…</div>}
      </div>

      {/* Tests */}
      <div className="inst-card">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h4 className="mb-0">Tests</h4>
          <button className="btn btn-primary btn-sm" disabled={!batch || batch.status !== 'Active'}
            onClick={() => navigate('/admin/BatchTestForm', { state: { batchId } })}>
            <i className="bx bxs-plus-circle" /> Add Test
          </button>
        </div>
        {batch && batch.status !== 'Active' && (
          <div className="text-muted mb-2">Batch must be Active before tests can be created.</div>
        )}
        <InstituteDataTable
          data={tests}
          rowKey="institute_test_id"
          columns={testColumns}
          order={[[2, 'desc']]}
          filename="Mensa_Batch_Tests"
          emptyText="No tests yet"
          onAction={(action, row) => {
            if (action === 'open') navigate('/admin/BatchTestDetail', { state: { instituteTestId: row.institute_test_id, batchId } });
          }}
        />
      </div>

      {/* Candidates */}
      <div className="inst-card">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h4 className="mb-0">Candidates</h4>
          <div>
            <button className="btn btn-primary btn-sm me-2" disabled={!batch || batch.status !== 'Active'}
              onClick={() => openCandidateAdd('add')}>
              <i className="bx bxs-plus-circle" /> Add Candidate
            </button>
            <button className="btn btn-outline-primary btn-sm" disabled={!batch || batch.status !== 'Active'}
              onClick={() => openCandidateAdd('bulk')}>
              Bulk Upload
            </button>
          </div>
        </div>

        <InstituteDataTable
          data={candidates}
          rowKey="_key"
          columns={candidateColumns}
          order={[[0, 'asc']]}
          filename="Mensa_Batch_Candidates"
          emptyText="No candidates registered"
          onAction={(action, row) => {
            if (action === 'assign') assignTest(row);
          }}
        />
      </div>
    </div>
  );
};

export default BatchDashboard;
